import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot,
  CalendarDays,
  Building2,
  Users,
  Ticket,
  PenSquare,
  LogOut,
  Menu,
  X,
  ShieldCheck,
  Columns3,
  CheckSquare,
  Briefcase,
  DollarSign,
  Receipt,
  Mail,
  Globe,
  Camera,
  Film,
  BarChart3,
  Activity as ActivityIcon,
  Clock,
  LayoutDashboard,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { NEEDS_REPLY_EVENT } from '../../lib/communications';
import logo from '../../assets/logo.png';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { useConsoleTheme } from '../../lib/theme';

function navItemsForRole(role, badges = {}) {
  // Sprint I D1: Prospects entry removed — covered by /admin/accounts?stage_view=prospect.
  // Sprint J: Dashboard at the top — admin command-center landing.
  const items = [
    { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/pipeline', label: 'Pipeline', icon: Columns3 },
    { to: '/admin/accounts', label: 'Accounts', icon: Building2 },
    { to: '/admin/projects', label: 'Projects', icon: Briefcase },
    { to: '/admin/shoots', label: 'Shoots', icon: Camera },
    { to: '/admin/media', label: 'Media', icon: Film },
    { to: '/admin/communications', label: 'Communications', icon: Mail, count: badges.needsReply || 0 },
    { to: '/admin/audits', label: 'Audits', icon: ShieldCheck },
    { to: '/admin/domains', label: 'Domains', icon: Globe },
    { to: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { to: '/admin/tasks', label: 'Tasks', icon: CheckSquare },
    { to: '/admin/calendar', label: 'Calendar', icon: CalendarDays },
    { to: '/admin/assistant', label: 'Assistant', icon: Bot },
  ];
  // Financial dashboard (aggregate KPIs, AR aging, revenue) stays admin-only —
  // route returns 403 for ops, so we hide the nav link too. Sprint K opens
  // Invoices to ops since they handle invoice creation + maintenance day-to-day.
  if (role === 'nexsav_admin') {
    items.push({ to: '/admin/financial', label: 'Financial', icon: DollarSign });
  }
  if (role === 'nexsav_admin' || role === 'ops') {
    items.push({ to: '/admin/invoices', label: 'Invoices', icon: Receipt });
  }
  items.push({ divider: true });
  items.push({ to: '/admin/users', label: 'Users', icon: Users });
  items.push({ to: '/admin/tickets', label: 'Tickets', icon: Ticket, badge: !!badges.ticketsUnresolved });
  // Sprint I D2: SOON flag removed — admin edit-request review UI now wired.
  items.push({ to: '/admin/edit-requests', label: 'Edit Requests', icon: PenSquare });
  // Sprint I D7: activity feed.
  items.push({ to: '/admin/activity', label: 'Activity', icon: ActivityIcon });
  // Sprint I D8: system → crons.
  items.push({ to: '/admin/system/crons', label: 'Crons', icon: Clock });
  // F13: system → errors (application error log).
  items.push({ to: '/admin/system/errors', label: 'Errors', icon: AlertTriangle });
  return items;
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useConsoleTheme(); // marks <html> with the console theme while mounted
  // Sidebar nav-item badges. Currently only Tickets has one — a red dot
  // when there are open or in_progress tickets (mirrors the portal's
  // unsigned-invoice red dot pattern). Refetched on every route change so
  // the dot disappears as soon as the admin transitions a ticket to
  // resolved/closed.
  const [badges, setBadges] = useState({ ticketsUnresolved: false, needsReply: 0 });

  useEffect(() => {
    let alive = true;
    // Guard for test environments where api.get may not be a real promise.
    const p = api.get('/api/admin/tickets');
    if (!p || typeof p.then !== 'function') return undefined;
    p.then((d) => {
      if (!alive) return;
      const count = Number(d?.unresolved_count) || 0;
      setBadges((prev) => ({ ...prev, ticketsUnresolved: count > 0 }));
    }).catch(() => {});
    return () => { alive = false; };
  }, [location.pathname]);

  // Communications → amber "Needs reply" count. Refetched on every route
  // change and whenever a reply/resolve announces itself (same tab).
  useEffect(() => {
    let alive = true;
    const run = () => {
      const p = api.get('/api/admin/communications/needs-reply?count=1');
      if (!p || typeof p.then !== 'function') return;
      p.then((d) => {
        if (!alive) return;
        setBadges((prev) => ({ ...prev, needsReply: Number(d?.total) || 0 }));
      }).catch(() => {});
    };
    run();
    window.addEventListener(NEEDS_REPLY_EVENT, run);
    return () => { alive = false; window.removeEventListener(NEEDS_REPLY_EVENT, run); };
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="portal-skin min-h-screen relative">
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grain-bg opacity-[0.13]" />
      </div>

      <div className="relative z-[1] flex min-h-screen">
        <Sidebar role={user?.role} badges={badges} />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="flex items-center justify-between px-4 md:px-8 py-4 border-b border-white/[0.05] bg-[#0a0a0f]/60 backdrop-blur-md sticky top-0 z-20">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.05] transition-colors"
                aria-label="Open navigation"
              >
                <Menu className="w-4 h-4" />
              </button>
              <div className="md:hidden flex items-center gap-2">
                <img src={logo} alt="NexSav" data-light-invert className="h-6 w-auto opacity-90" />
                <span className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">Admin</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <div className="text-sm text-white">{user?.name}</div>
                <div className="text-[11px] text-gray-500">{user?.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <button
                  onClick={onLogout}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] text-[13px] text-gray-300 hover:text-white hover:border-white/[0.15] transition-all"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-4 md:px-8 py-8 md:py-10">
            <Outlet />
          </main>
        </div>
      </div>

      <MobileSidebar open={mobileOpen} onClose={() => setMobileOpen(false)} role={user?.role} badges={badges} />
    </div>
  );
}

function Sidebar({ role, badges }) {
  const NAV_ITEMS = navItemsForRole(role, badges);
  return (
    <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-white/[0.05] bg-[#0a0a0f]/50 backdrop-blur-md">
      <div className="px-5 py-5 border-b border-white/[0.05]">
        <Link to="/" className="flex items-center gap-3 group">
          <img src={logo} alt="NexSav" data-light-invert className="h-7 w-auto opacity-90 group-hover:opacity-100 transition-opacity" />
          <div>
            <div className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase flex items-center gap-1.5">
              <ShieldCheck className="w-3 h-3" />
              Admin Panel
            </div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 px-3 py-4 flex flex-col gap-0.5">
        {NAV_ITEMS.map((item, i) => (
          item.divider
            ? <div key={`div-${i}`} className="my-2 border-t border-white/[0.05]" />
            : <NavLinkItem key={item.to} item={item} />
        ))}
      </nav>
      <div className="px-5 py-4 border-t border-white/[0.05]">
        <Link
          to="/"
          className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors"
        >
          ← Back to nexsav.net
        </Link>
      </div>
    </aside>
  );
}

function MobileSidebar({ open, onClose, role, badges }) {
  const NAV_ITEMS = navItemsForRole(role, badges);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 md:hidden"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative w-72 h-full bg-[#0a0a0f] border-r border-white/[0.08] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-white/[0.05]">
              <Link to="/" onClick={onClose} className="flex items-center gap-3">
                <img src={logo} alt="NexSav" data-light-invert className="h-7 w-auto opacity-90" />
                <span className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase">Admin</span>
              </Link>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.05]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-0.5" onClick={onClose}>
              {NAV_ITEMS.map((item, i) => (
                item.divider
                  ? <div key={`div-${i}`} className="my-2 border-t border-white/[0.05]" />
                  : <NavLinkItem key={item.to} item={item} />
              ))}
            </nav>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function NavLinkItem({ item }) {
  const { icon: Icon, label, to, soon, badge, count } = item;
  if (soon) {
    return (
      <NavLink
        to={to}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
            isActive
              ? 'bg-white/[0.05] text-white border border-white/[0.06]'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.02] border border-transparent'
          }`
        }
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1">{label}</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-gray-500 tracking-wider uppercase">
          Soon
        </span>
      </NavLink>
    );
  }
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
          isActive
            ? 'bg-gradient-to-r from-[#B0FC38]/[0.12] to-transparent text-white border border-[#B0FC38]/20'
            : 'text-gray-400 hover:text-white hover:bg-white/[0.03] border border-transparent'
        }`
      }
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1">{label}</span>
      {badge && (
        <span data-testid={`nav-badge-${label.toLowerCase()}`} className="w-2 h-2 rounded-full bg-red-400 shadow shadow-red-500/40" />
      )}
      {count > 0 && (
        <span
          data-testid={`nav-count-${label.toLowerCase()}`}
          aria-label={`${count} waiting for a reply`}
          className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 tabular-nums"
        >
          {count}
        </span>
      )}
    </NavLink>
  );
}
